import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { AppPicker } from './AppPicker.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface ReportRow {
  readonly reportConfigId: string;
  readonly reportCode: string;
  readonly reportName: string;
  readonly reportType: string;
  readonly status: string;
  readonly versionNo: number;
}

const REPORT_TYPES = ['TABULAR', 'SUMMARY', 'ANALYTICAL', 'OPERATIONAL', 'COMPLIANCE', 'EXECUTIVE'];
const STATUSES = ['DRAFT', 'PUBLISHED', 'ARCHIVED'];

/**
 * M40 — Report Builder (§14). The report authoring surface over T530. Define a
 * report of one of the six archetypes (columns, filters, grouping) under an
 * application. Viewing needs REPORT_VIEW; authoring needs REPORT_MANAGE.
 */
export function ReportBuilder(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [applicationId, setApplicationId] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [rows, setRows] = useState<ReportRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [reportName, setReportName] = useState('');
  const [reportType, setReportType] = useState('TABULAR');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (applicationId) params.set('applicationId', applicationId);
    if (typeFilter) params.set('reportType', typeFilter);
    if (statusFilter) params.set('status', statusFilter);
    params.set('pageSize', '100');
    apiRequest<{ data: ReportRow[]; total: number }>(`/v1/app-platform/reports?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, applicationId, typeFilter, statusFilter, reload]);

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!applicationId.trim()) { setActionError(t('m40.common.appIdRequired')); return; }
    if (!reportName.trim()) { setActionError(t('m40.reportBuilder.nameRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: ReportRow }>(`/v1/app-platform/reports`, {
        method: 'POST',
        body: { applicationId: applicationId.trim(), reportName: reportName.trim(), reportType },
        token,
      });
      setNotice(t('m40.common.created', { code: r.data.reportCode }));
      setReportName('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.reportBuilder.notAuth'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: ReportRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/app-platform/reports/${row.reportConfigId}`, { method: 'DELETE', token });
      setNotice(t('m40.common.removed', { code: row.reportCode }));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.reportBuilder.notAuth'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="report-builder">
      <h1>{t('m40.reportBuilder.title')}</h1>

      <section aria-label="report-scope">
        <AppPicker label={t('m40.common.applicationId')} value={applicationId} onPick={setApplicationId} />
      </section>

      <section aria-label="create-report">
        <h2>{t('m40.reportBuilder.newReport')}</h2>
        <form aria-label="report-form" onSubmit={(e) => void create(e)}>
          <label>{t('m40.common.name')}<input aria-label="field-report-name" value={reportName} onChange={(e) => setReportName(e.target.value)} /></label>
          <label>{t('m40.common.type')}
            <select aria-label="field-report-type" value={reportType} onChange={(e) => setReportType(e.target.value)}>
              {REPORT_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-report" disabled={busy}>{busy ? t('m40.common.creating') : t('m40.reportBuilder.createBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="report-error">{actionError}</p>}
        {notice && <p aria-label="report-notice">{notice}</p>}
      </section>

      <section aria-label="report-filters">
        <label>{t('m40.common.type')}
          <select aria-label="filter-type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">{t('m40.common.allTypes')}</option>
            {REPORT_TYPES.map((ty) => <option key={ty} value={ty}>{ty}</option>)}
          </select>
        </label>
        <label>{t('m40.common.status')}
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{t('m40.common.allStates')}</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m40.common.loading')}</p> : (
        <>
          <p aria-label="report-total">{t('m40.reportBuilder.total', { count: rows.length })}</p>
          <DataTable<ReportRow>
            ariaLabel="report-table"
            testId="report-table"
            rowKey={(r) => r.reportConfigId}
            rowDataAttrs={(r) => ({ 'data-report': r.reportCode })}
            emptyMessage={t('m40.reportBuilder.empty')}
            rows={rows}
            columns={[
              { key: 'code', header: t('m40.common.code'), render: (r) => r.reportCode },
              { key: 'name', header: t('m40.common.name'), render: (r) => r.reportName },
              { key: 'type', header: t('m40.common.type'), render: (r) => r.reportType },
              { key: 'status', header: t('m40.common.status'), cellAriaLabel: (r) => `status-${r.reportCode}`, render: (r) => r.status },
              { key: 'version', header: t('m40.common.version'), render: (r) => `v${r.versionNo}` },
              { key: 'actions', header: t('m40.common.actions'), render: (r) => (
                <Button variant="danger" type="button" aria-label={`remove-${r.reportCode}`} onClick={() => void remove(r)}>{t('m40.common.remove')}</Button>
              ) },
            ]}
          />
        </>
      )}
    </main>
  );
}
